import React from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

import { ListSelect } from "@common/listSelect";
import { TextInput } from "@common/textInput";
import { RichTextInput } from "@common/richTextInput";
import { DateTimePicker } from "@common/dateTimePicker";
import { Props } from "./types";
import { ParticipantsList } from "./participantsList";
import styles from "./styles.scss";
import { ProjectsSelection } from "@common/projectsSelection";

function EventCreationForm({
  formData,
  project,
  lists,
  list,
  error,
  editForm,
  isMultiProject,
  setList,
  selectAllDay,
  setSelectAllDay,
  onProjectChange,
}: Props) {
  const { t } = useTranslation();

  return (
    <div className={styles.eventCreationForm}>
      {isMultiProject && (
        <div className={styles.projectSelectionRow}>
          <div className={styles.group}>
            <h3 className={styles.title}>{t("eventCreation.inProject")}</h3>
            <ProjectsSelection
              placeholder={t("eventCreation.projectSelectionPlaceholder")}
              onSelectProject={(selectedProject) => {
                onProjectChange?.(selectedProject);
              }}
            />
          </div>
        </div>
      )}
      <div className={styles.group}>
        <h3 className={styles.title}>{t("eventCreation.parentListTitle")}</h3>
        <div className={styles.listSelect}>
          <ListSelect
            lists={lists}
            selectedList={list}
            selectList={(nextList) => {
              setList(nextList);
            }}
          />
        </div>
      </div>
      <div className={styles.row}>
        <div className={styles.group}>
          <h3 className={styles.title}>{t("eventCreation.nameTitle")}</h3>
          <TextInput
            value={formData.name}
            className={styles.textInput}
            placeholder={t("eventCreation.namePlaceholder")}
            input={(name) => editForm({ ...formData, name })}
            isInvalid={error.duplicateName || error.emptyName}
            autoFocus
          />
        </div>
      </div>
      <div className={styles.dateGroup}>
        <div className={styles.row}>
          <h3 className={styles.title}>{t("eventCreation.startDateTitle")}</h3>
          <div className={styles.dateTimeInput}>
            <DateTimePicker
              dateTime={formData.startTime}
              projectTimezone={project.timeZone}
              placeholder={t("eventCreation.startDatePlaceholder")}
              selectAllDay={selectAllDay}
              isEndDate={false}
              input={(startTime) => {
                const startDate = new Date(startTime);
                editForm({
                  ...formData,
                  startTime,
                  endTime:
                    !formData.endTime || new Date(formData.endTime) <= startDate
                      ? new Date(
                          startDate.setHours(startDate.getHours() + 1),
                        ).toISOString()
                      : formData.endTime,
                });
              }}
              clear={() => editForm({ ...formData, startTime: "" })}
              isInvalid={error.startDateOutOfBound || error.invalidDateRange}
              setSelectAllDay={setSelectAllDay}
            />
          </div>
        </div>
        <div className={styles.row}>
          <h3 className={styles.title}>{t("eventCreation.endDateTitle")}</h3>
          <div className={styles.dateTimeInput}>
            <DateTimePicker
              dateTime={formData.endTime}
              projectTimezone={project.timeZone}
              placeholder={t("eventCreation.endDatePlaceholder")}
              selectAllDay={selectAllDay}
              isEndDate
              input={(endTime) => {
                const date = new Date(endTime);

                editForm({
                  ...formData,
                  endTime,
                  startTime: !formData.startTime
                    ? new Date(
                        date.getFullYear(),
                        date.getMonth(),
                        date.getDate(),
                      ).toISOString()
                    : formData.startTime,
                });
              }}
              clear={() => editForm({ ...formData, endTime: "" })}
              isInvalid={error.invalidDateRange}
              setSelectAllDay={setSelectAllDay}
            />
          </div>
        </div>
      </div>
      <div className={styles.selectAllDayBox}>
        <div
          onClick={() => setSelectAllDay(!selectAllDay)}
          className={classNames(styles.checkbox, {
            [styles.checked]: selectAllDay,
          })}
        />
        <p className={styles.selectAllDayDescription}>
          {t("eventCreation.selectAllDay")}
        </p>
      </div>
      <div className={styles.group}>
        <h3 className={styles.title}>
          {t("eventCreation.assignedMembersTitle")}
        </h3>
        <div className={styles.participants}>
          <ParticipantsList
            project={project}
            participants={formData.participants}
            select={(resources) =>
              editForm({
                ...formData,
                participants: [...formData.participants, ...resources],
              })
            }
            remove={(resources) =>
              editForm({
                ...formData,
                participants: formData.participants.filter(
                  ({ uuid }) =>
                    !resources.find((resource) => uuid === resource.uuid),
                ),
              })
            }
          />
        </div>
      </div>
      <div className={styles.group}>
        <h3 className={styles.title}>{t("eventCreation.descriptionTitle")}</h3>
        <RichTextInput
          value={formData.description}
          maxLength={10000}
          placeholder={t("eventCreation.descriptionPlaceholder")}
          input={(description) => editForm({ ...formData, description })}
        />
      </div>
    </div>
  );
}

export { EventCreationForm };
